import React, { useEffect, useRef, useState } from 'react'
import { CustomTabPanel } from '.';
import { Autocomplete, Box, Chip, CircularProgress, FormControl, IconButton, InputLabel, MenuItem, Modal, Select, TextField, Typography } from '@mui/material';
import Grid from "@mui/material/Grid2";
import { DataGrid } from '@mui/x-data-grid';
import { tableStyles } from 'src/shared/util/tableStyles';
import moment from "moment";
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import VrpanoIcon from '@mui/icons-material/Vrpano';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from "@mui/icons-material/Close";
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { formatDate } from "src/shared/util/functions";
import TocIcon from '@mui/icons-material/Toc';
import AppsIcon from '@mui/icons-material/Apps';
import DownloadIcon from '@mui/icons-material/Download';
import Swal from "sweetalert2";
import JSZip from "jszip";

export interface FilesViewerProps {
    value: any;
    start: any;
    end: any;
    filesForDataRange: any[];
}

const internalTabs = [
    { "name": "table", "value": "table", icon: <TocIcon /> },
    { "name": "cards", "value": "cards", icon: <AppsIcon /> }
]

export const FilesViewer = ({
    value, start, end, filesForDataRange
}: FilesViewerProps) => {

    const [filteredRows, setFilteredRows] = useState<any>([]);

    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        uploadedBy: "",
        tags: [] as string[],
        description: "",
        search: "",
        project: "",
    });

    const [columns, setColumns] = useState<any[]>([
        {
            field: "associatedActivityDate",
            headerName: "Fecha",
            align: "center",
            headerAlign: "center",
            width: 130,
            renderCell: (params: any) => {
                return (
                    <Box
                        sx={{
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Typography
                            sx={{
                                width: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "normal",
                                wordBreak: "break-word",
                                lineHeight: "1.4",
                                py: "5px",
                                px: "2px",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                display: "-webkit-box",
                            }}
                        >
                            {params.value ? moment(params.value).format("DD-MM-YYYY") : ""}
                        </Typography>
                    </Box>
                )
            },
        },
        {
            field: "locationName",
            headerName: "Pozo",
            width: 100,
        },
        {
            field: "projectName",
            headerName: "Campo",
            flex: 1,
        },
        {
            field: "associatedActivityType",
            headerName: "Tipo de actividad",
            flex: 1,
        },
        {
            field: "executedUser",
            headerName: "Ingenieros",
            flex: 1,
        },
        {
            field: "groupAssigned",
            headerName: "Cuadrilla",
            width: 110,
        },
        {
            field: "fileTags",
            headerName: "Etiquetas",
            flex: 1,
            headerAlign: "center",
            align: "center",
            renderCell: (params: any) => (
                <Grid
                    sx={{
                        display: "flex",
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: "100%",
                        margin: 1,
                        flexWrap: "wrap",
                        gap: 0.5,
                    }}
                >
                    {params.row.fileTags.map((tag: string, index: number) => (
                        <Chip
                            key={index}
                            label={tag}
                            size="small"
                            sx={{ mb: 0.5 }}
                        />
                    ))}
                </Grid>
            ),
        },
        {
            field: "actions",
            headerName: "",
            width: 160,
            headerAlign: "center",
            align: "center",
            renderCell: (params: any) => {
                return (
                    <Grid
                        sx={{
                            display: "flex",
                            justifyContent: "space-around",
                            height: "100%",
                        }}
                    >
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                                handleDeleteRow(params.row)
                            }
                        >
                            <DeleteForeverIcon fontSize="medium" />
                        </IconButton>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                                handleShowFileModal(params.row)
                            }
                        >
                            <VrpanoIcon fontSize="medium" />
                        </IconButton>
                    </Grid>
                );
            },
        }
    ])

    const cardsFields = [
        { label: "Fecha", value: "associatedActivityDate" },
        { label: "Pozo", value: "locationName" },
        { label: "Campo", value: "projectName" },
        { label: "Tipo de actividad", value: "associatedActivityType" },
        { label: "Etiquetas", value: "fileTags" },
    ]

    const [tags, setTags] = useState<string[]>([]);

    const [projectsName, setProjectsName] = useState<string[]>([]);

    const [users, setUsers] = useState<string[]>([]);

    const [activeFile, setActiveFile] = useState<any | null>(null);

    const [activeSummaryView, setactiveSummaryView] = useState(internalTabs[0].value)

    const imgRef = useRef<HTMLImageElement>(null);

    const handleDeleteRow = (id: any) => {
        // console.log("Delete row with id:", id);
        Swal.fire({
            title: "Error",
            html: `Opcion No implementada`,
            icon: "error",
            confirmButtonText: "Aceptar",
        })
    }

    const handleShowFileModal = (file: any) => {

        setActiveFile(file);
    }

    const onCloseModal = () => {
        setActiveFile(null);
    }

    const onDownloadFiles = async () => {
        const zip = new JSZip();

        setLoading(true);

        try {
            // Get all file URLs and names from filteredRows
            const filePromises = filteredRows.map(async (row: any) => {
                try {
                    const response = await fetch(row.fileUrl);
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    const fileBuffer = await response.arrayBuffer();
                    // Use fileName or fallback to fileId
                    let fileName = row.fileName
                        ? row.fileName
                        : `file_${row.fileId}`;

                    if (!fileName.includes(row.locationName)) {
                        fileName = `${row.locationName}_${fileName}`;
                    }

                    // Try to get extension from URL
                    const extMatch = row.fileUrl.match(/\.(\w+)(\?|$)/);
                    const ext = extMatch ? extMatch[1] : "jpg";
                    zip.file(`${fileName}.${ext}`, fileBuffer);
                    // console.log(`✅ Archivo '${fileName}.${ext}' descargado y añadido.`);
                } catch (error) {
                    console.error(`❌ Error al descargar ${row.fileUrl}:`, error);
                }
            });

            await Promise.allSettled(filePromises);

            const zipBlob = await zip.generateAsync({ type: "blob" });
            const startDate = start ? moment(start).format("YYYY-MM-DD") : "";
            const endDate = end ? moment(end).format("YYYY-MM-DD") : "";
            const zipName = `archivos_${startDate}_${endDate}.zip`;

            // Download the zip file in browser
            const link = document.createElement("a");
            link.href = URL.createObjectURL(zipBlob);
            link.download = zipName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {

        } finally {
            setLoading(false);
        }


    };

    useEffect(() => {

        if (!filesForDataRange || filesForDataRange.length === 0) {
            setFilteredRows([]);
            return;
        }

        let newFilteredRows = filesForDataRange.filter(row =>
            (filters.uploadedBy !== "" ? row.executedUser === filters.uploadedBy : true) &&
            (filters.tags?.length > 0 ? row.fileTags.some((tag: any) => filters.tags.includes(tag)) : true) &&
            (filters.project !== "" ? row.projectName === filters.project : true) &&
            (filters.search !== "" ? row.description.toLowerCase().includes(filters.search.toLowerCase())
                || row.fileName.toLowerCase().includes(filters.search.toLowerCase())
                || row.locationName.toLowerCase().includes(filters.search.toLowerCase())
                : true)
        );

        setFilteredRows(newFilteredRows);

        const uniqueTags = Array.from(new Set(filesForDataRange.flatMap(file => file.fileTags)));

        setTags(uniqueTags);

        const uniqueProjects = Array.from(new Set(filesForDataRange.map(file => file.projectName)));

        setProjectsName([...uniqueProjects]);

        const uniqueUsers = Array.from(new Set(filesForDataRange.map(file => file.executedUser)));

        setUsers([...uniqueUsers]);

    }, [filesForDataRange, filters])

    return (
        <>
            <CustomTabPanel value={value} index={1}>
                {loading && (
                    <Grid className="card_loading_container">
                        <CircularProgress size="70px" />
                        <Typography fontSize={30} fontWeight={500}>Consolidando Imagenes...</Typography>
                    </Grid>
                )}
                <Box>
                    <Grid className="file_preview_search_box_input">
                        <TextField
                            className="input_search"
                            placeholder="Buscar en archivos por nombre o descripción"
                            size="small"
                            variant="outlined"
                            value={filters.search}
                            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
                            sx={{
                                width: "100%",
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        border: 0,
                                    },
                                    "&:hover fieldset": {
                                        border: 0,
                                    },
                                    "&.Mui-focused fieldset": {
                                        border: 0,
                                    },
                                },
                            }}
                        />
                        <Grid className="filters_list_container">
                            <FormControl sx={{ display: "flex", flexDirection: "row", lignItems: "center" }}>
                                <InputLabel>Subido Por</InputLabel>
                                <Select
                                    value={filters.uploadedBy}
                                    onChange={e => setFilters(f => ({ ...f, uploadedBy: e.target.value }))}
                                    displayEmpty
                                    label="Subido por"
                                    size="small"
                                    className="select_filter"
                                >
                                    {users?.map((user, index) => <MenuItem key={index} value={user}>{user}</MenuItem>)}
                                </Select>
                                {filters.uploadedBy && (
                                    <IconButton color="warning" onClick={() => setFilters(f => ({ ...f, uploadedBy: "" }))}>
                                        <ClearIcon />
                                    </IconButton>
                                )}
                            </FormControl>
                            <FormControl>
                                <Autocomplete
                                    multiple
                                    options={tags}
                                    getOptionLabel={(option) => option}
                                    value={tags.filter(tag => filters.tags.includes(tag))}
                                    className="select_filter"
                                    onChange={(event, newValue) => {
                                        setFilters(f => ({
                                            ...f,
                                            tags: newValue.map(tag => tag)
                                        }));
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Etiquetas" size="small" fullWidth />
                                    )}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option}>
                                            {/* <Chip label={option} size="small" sx={{ mr: 1 }} /> */}
                                            {option}
                                        </li>
                                    )}
                                />
                            </FormControl>
                            <FormControl sx={{ display: "flex", flexDirection: "row", lignItems: "center" }}>
                                <InputLabel>Campo</InputLabel>
                                <Select
                                    value={filters.project}
                                    onChange={e => setFilters(f => ({ ...f, project: e.target.value }))}
                                    displayEmpty
                                    label="Campo"
                                    size="small"
                                    className="select_filter"
                                >
                                    {projectsName?.map(project => <MenuItem key={project} value={project}>{project}</MenuItem>)}
                                </Select>
                                {filters.project && (
                                    <IconButton color="warning" onClick={() => setFilters(f => ({ ...f, project: "" }))}>
                                        <ClearIcon />
                                    </IconButton>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid className="buttons_container">
                        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 3, gap: 2 }}>
                            {
                                internalTabs.map((item, index) => {
                                    return (
                                        <IconButton
                                            onClick={() => setactiveSummaryView(item.value)}
                                            key={index}
                                            className="active_button"
                                            sx={{
                                                backgroundColor: activeSummaryView === item.value ? "#1976d2" : "#f0f0f0",
                                                color: activeSummaryView === item.value ? "#fff" : "#000",
                                                "&:hover": {
                                                    backgroundColor: activeSummaryView === item.value ? "#42a5f5" : "#f0f0f0",
                                                },
                                            }}
                                        >
                                            {item.icon}
                                        </IconButton>
                                    )
                                })
                            }
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "flex-start", mt: 3, gap: 2 }}>
                            <IconButton
                                onClick={() => onDownloadFiles()}
                            >
                                <DownloadIcon />
                            </IconButton>
                        </Box>
                    </Grid>
                    {activeSummaryView === "table" && (
                        <DataGrid
                            rows={filteredRows}
                            columns={columns}
                            pageSizeOptions={[10, 20, 50, 100]}
                            getRowHeight={() => 'auto'}
                            sx={{
                                ...tableStyles,
                                '& .MuiDataGrid-cell': {
                                    textAlign: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                    // justifyContent: 'center',
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    textAlign: 'center',
                                },
                            }}
                            disableRowSelectionOnClick
                            getRowId={(row) => row.fileId}
                        />
                    )}

                    {activeSummaryView === "cards" && (
                        <Grid className="cards_container">
                            {filteredRows.map((file: any, index: number) => (
                                <Grid key={file.fileId} className="card_item">
                                    <img src={file.fileUrl} alt={file.fileName} className="card_image" />
                                    <Grid className="card_content">
                                        {cardsFields.map((field) => (
                                            <Typography key={field.value} className="card_text">
                                                <strong>{field.label}:</strong>
                                                {field.value === "associatedActivityDate"
                                                    ? (file[field.value] ? moment(file[field.value]).format("DD-MM-YYYY") : "")
                                                    : field.value === "fileTags"
                                                        ? (file[field.value] && file[field.value].map((tag: string, index: number) => 
                                                        <Chip
                                                            key={index}
                                                            label={tag}
                                                            size="small"
                                                            sx={{ mb: 0.5 }}
                                                        />))
                                                        : file[field.value]}
                                            </Typography>
                                        ))}
                                    </Grid>
                                    <Grid className="card_actions">
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                                handleDeleteRow(file)
                                            }
                                        >
                                            <DeleteForeverIcon fontSize="medium" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() =>
                                                handleShowFileModal(file)
                                            }
                                        >
                                            <VrpanoIcon fontSize="medium" />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    )}

                </Box>
            </CustomTabPanel>
            <Modal open={Boolean(activeFile)} onClose={onCloseModal}>
                <Box className="file-preview-modal">
                    <Box className="header">
                        <Typography className="title">Previsualizador de Archivos</Typography>
                        <IconButton className="close-btn" onClick={onCloseModal}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <>
                        <Box className="body">
                            <Box className="left">
                                <TextField label="Nombre" value={activeFile?.fileName || ""} size="small" disabled fullWidth />
                                <TextField label="Tipo" value={activeFile?.fileType || ""} size="small" disabled fullWidth />
                                <TextField
                                    label="Actividad"
                                    value={activeFile?.associatedActivityType || ""}
                                    size="small"
                                    disabled
                                    fullWidth
                                />
                                <TextField
                                    label="Tag"
                                    value={activeFile?.fileTags || ""}
                                    size="small"
                                    disabled
                                    fullWidth
                                />
                                <TextField
                                    label="Descripción"
                                    value={activeFile?.description || ""}
                                    fullWidth
                                    disabled
                                    multiline
                                    rows={4}
                                    size="small"
                                />
                            </Box>
                            <Box className="right">
                                <IconButton
                                    className="download-btn"
                                    onClick={() => window.open(activeFile?.fileUrl || "", "_blank")}
                                    title="Descargar archivo"
                                >
                                    <AspectRatioIcon />
                                </IconButton>
                                {(activeFile?.latitude && activeFile?.longitude) ? (
                                    <IconButton
                                        className="map-btn"
                                        onClick={() => window.open(`https://www.google.com/maps?q=${activeFile.latitude},${activeFile.longitude}`, "_blank")}
                                        title="Ver en mapa"
                                    >
                                        <TravelExploreIcon />
                                    </IconButton>
                                ) : (
                                    <></>
                                )}
                                <Grid className="preview-img-external-container">
                                    <Grid>
                                        <img ref={imgRef} src={activeFile?.fileUrl} alt={activeFile?.fileName} className="preview-img" />
                                        {activeFile?.associatedActivityDate && (
                                            <Grid className="preview-img-text-bottom-container">
                                                <Typography className="preview-img-text-bottom">
                                                    {formatDate(activeFile?.associatedActivityDate, "dd-MMM-yy")}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>

                                </Grid>
                            </Box>
                        </Box>
                    </>
                </Box>
            </Modal>
        </>
    )
}
